import React, { useState } from "react";
import { MESSAGES } from "../../constants/const";
import { showErrorMessage } from "../appPane";
import type { OrderTableRow } from "../../types";
import { getInputDateString } from "../../logic/dateUtils";
import ModalHeader from "./modalHeader";

interface Props {
  order: OrderTableRow;
  onClose: () => void;
  onEdit: (date: string, time: string, timeEnd: string, volume: string) => Promise<void>;
}

// 30分刻みの時刻選択肢を生成する（例: ["00:00", "00:30", "01:00", ...]）
const THIRTY_MIN_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

// セレクトボックスの表示値を判定する
const getTimeSelectValue = (orderTime: string, orderTimeEnd: string, isTimeRange: boolean): string => {
  if (isTimeRange) return "時間帯指定";
  if (orderTime === "AM" || orderTime === "PM") return orderTime;
  return "指定なし";
};

// 既存データ（開始・終了時刻）からstateの初期値を決定する
// ルール:
// ① 開始00:00 終了00:00 => 指定なし
// ② 開始00:00 終了12:00 => AM
// ③ 開始12:01 終了23:59 => PM
// ④ それ以外 => 時間帯指定（開始/終了をそのまま使用）
const resolveInitialTimeState = (
  scheduledTime: string,
  scheduledTimeEnd: string
): { orderTime: string; orderTimeEnd: string; isTimeRange: boolean } => {
  if (scheduledTime === "00:00" && scheduledTimeEnd === "00:00")
    return { orderTime: "", orderTimeEnd: "", isTimeRange: false };
  if (scheduledTime === "00:00" && scheduledTimeEnd === "12:00")
    return { orderTime: "AM", orderTimeEnd: "", isTimeRange: false };
  if (scheduledTime === "12:01" && scheduledTimeEnd === "23:59")
    return { orderTime: "PM", orderTimeEnd: "", isTimeRange: false };
  if (scheduledTime && scheduledTime !== "00:00")
    return { orderTime: scheduledTime, orderTimeEnd: scheduledTimeEnd ?? "", isTimeRange: true };
  return { orderTime: "", orderTimeEnd: "", isTimeRange: false };
};

/**
 * 発注情報編集ポップアップ
 * @param props
 * @returns
 */
export const OrderChangeModal: React.FC<Props> = ({ order, onClose, onEdit }) => {
  // 入力フォームの値
  const [date, setDate] = useState(getInputDateString(new Date(order.scheduledDate)));
  const initialTimeState = resolveInitialTimeState(order.scheduledTime, order.scheduledTimeEnd);
  const [editTime, setEditTime] = useState(initialTimeState.orderTime);
  const [editTimeEnd, setEditTimeEnd] = useState(initialTimeState.orderTimeEnd);
  const [isTimeRange, setIsTimeRange] = useState(initialTimeState.isTimeRange);
  const [volume, setVolume] = useState(order.orderVolume?.toString() ?? "");
  const today = new Date();

  // フォーム送信
  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (date === "" || volume === "") {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }

    // 選択肢に応じて送信する開始・終了時刻を決定する
    // ①AM → 00:00 〜 12:00
    // ②PM → 12:01 〜 23:59
    // ③指定なし → 00:00 〜 00:00
    // ④時間帯指定 → 入力値をそのまま使用
    const selectValue = getTimeSelectValue(editTime, editTimeEnd, isTimeRange);
    const resolvedTime =
      selectValue === "AM" ? "00:00" :
      selectValue === "PM" ? "12:01" :
      selectValue === "指定なし" ? "00:00" :
      editTime;
    const resolvedTimeEnd =
      selectValue === "AM" ? "12:00" :
      selectValue === "PM" ? "23:59" :
      selectValue === "指定なし" ? "00:00" :
      editTimeEnd;

    onEdit(date, resolvedTime, resolvedTimeEnd, volume);
  };

  return (
    <div className="modal">
      <div className="content">
        <ModalHeader title="発注登録" onClose={onClose} />
        <p>登録日：{order.orderAddDate}</p>
        <p>販売業者：{order.vendorName}</p>
        <p>薬剤：{order.chemicalName}</p>

        <form onSubmit={sendForm}>
          <div className="parts">
            <label>納入予定日：</label>
            <input
              type="date"
              value={date}
              min={getInputDateString(today)}
              onChange={e => setDate(e.target.value)}
              required
            />
          </div>

          <div className="parts">
            <label>納入予定時刻：</label>
            <select
              className="grid-cell-item"
              value={getTimeSelectValue(editTime, editTimeEnd, isTimeRange)}
              onChange={e => {
                const val = e.target.value;
                if (val === "指定なし") {
                  setEditTime("");
                  setEditTimeEnd("");
                  setIsTimeRange(false);
                } else if (val === "AM" || val === "PM") {
                  setEditTime(val);
                  setEditTimeEnd("");
                  setIsTimeRange(false);
                } else {
                  setEditTime("");
                  setEditTimeEnd("");
                  setIsTimeRange(true);
                }
              }}
            >
              <option>指定なし</option>
              <option>AM</option>
              <option>PM</option>
              <option>時間帯指定</option>
            </select>
            {getTimeSelectValue(editTime, editTimeEnd, isTimeRange) === "時間帯指定" && (
              <>
                <select
                  className="grid-cell-item"
                  value={editTime}
                  onChange={e => {
                    setEditTime(e.target.value);
                    // 開始時刻変更時、終了時刻が開始時刻以前になる場合はリセット
                    if (editTimeEnd && editTimeEnd < e.target.value) {
                      setEditTimeEnd("");
                    }
                  }}
                >
                  {THIRTY_MIN_OPTIONS.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                <span>〜</span>
                <select
                  className="grid-cell-item"
                  value={editTimeEnd}
                  onChange={e => setEditTimeEnd(e.target.value)}
                >
                  {/* 終了時刻は開始時刻以降の選択肢のみ表示 */}
                  {THIRTY_MIN_OPTIONS.filter(time => !editTime || time >= editTime).map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="parts">
            <label>納入数量({order.chemicalUnit2 || order.chemicalUnit1})：</label>
            <input
              type="number"
              min={0}
              step="any"
              value={volume}
              onChange={e => setVolume(e.target.value)}
              required
            />
            {order.chemicalUnit2 && (
              <div>
                ※1{order.chemicalUnit1}あたり{order.chemicalUnit2Calc}
                {order.chemicalUnit2}
              </div>
            )}
          </div>

          <div className="parts actions">
            <button onClick={onClose} className="negative">
              キャンセル
            </button>

            <button type="submit" className="positive">
              登録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderChangeModal;
